import React from 'react';
import { cn } from '@/lib/utils';
import { TableSortButton } from './TableSortButton';
import { TableSearchInput } from './TableSearchInput';
import { TableBooleanFilter } from './TableBooleanFilter';
import { TableDateRangeFilter } from './TableDateRangeFilter';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '@/components/ui/table';

export interface Column<T> {
    key: string;
    label: string;
    sortable?: boolean;
    filterable?: boolean;
    filterType?: 'text' | 'number' | 'boolean' | 'dateRange';
    filterPlaceholder?: string;
    dateRangeFromName?: string;
    dateRangeToName?: string;
    render?: (value: any, row: T) => React.ReactNode;
    /**
     * Alinierea coloanei. `right` aduce si cifre tabulare, deci coloanele de
     * numere se citesc pe verticala. Se pune automat de `columnBuilder.number()`
     * si de `createNumberColumn` — cele doua constructoare de coloane din repo.
     * → docs/design-system/REPORT_POLISH_PLAN.md, constatarea #8
     */
    align?: 'left' | 'right';
    /**
     * Ingheata coloana la marginea din stanga cand tabelul se deruleaza pe
     * orizontala. Se pune manual, pe coloana care identifica randul — NU pe
     * „prima coloana": prima poate fi conditionata de rol (vezi coloana
     * `preview` din `EditionStatusReportPage`, vizibila doar la ADMIN/DBEST),
     * deci un index fix ar ingheta altceva pentru fiecare rol.
     * Daca mai multe coloane o cer, doar prima e inghetata: toate ar sta la
     * `inset-inline-start: 0` si s-ar suprapune.
     * → docs/design-system/REPORT_POLISH_PLAN.md, faza 4
     */
    sticky?: boolean;
    /**
     * Rolul coloanei in rezumatul de pe mobil (`ReportMobileSummary`), acolo
     * unde nu se randeaza tabel deloc:
     * - `title` — textul care identifica randul (numele scolii, al elevului);
     * - `value` — cifra care se afiseaza in dreapta lui.
     *
     * Unitatea cifrei („kg", „elevi") NU e un rol de coloana: e o constanta a
     * raportului, nu o valoare din rand. Se da prin `unitLabel` pe
     * `ReportPageLayout`. Am incercat intai varianta cu rol de coloana si n-avea
     * niciun candidat real in cele sase rapoarte din jumatatea B.
     *
     * Se pune prin `options` pe oricare dintre constructoarele de coloane
     * (`createTextColumn(..., { cardRole: 'title' })`), fiindca toate fac spread
     * pe `...options` la final. `column-builder.ts` NU se atinge — nu are nevoie
     * de o metoda noua ca sa poarte campul asta.
     *
     * ⚠️ Fara `cardRole`, rezumatul cade pe prima coloana **de date**, nu pe
     * `columns[0]`: prima poate fi o coloana de actiuni conditionata de rol
     * (`preview` in `EditionStatusReportPage`), iar un index fix ar pune un
     * buton in locul numelui. Aceeasi capcana ca la `sticky`.
     */
    cardRole?: 'title' | 'value';

    /**
     * Randarea valorii in cardul din rezumatul mobil, cand `render`-ul de celula
     * nu e potrivit acolo.
     *
     * ⚠️ De ce exista: un `render` de celula poate intoarce ORICE, inclusiv
     * controale. Coloana „Puncte" din raportul de activitati intoarce cifra plus
     * un `Popover` cu buton (nota de plafon) — corect intr-un tabel, gresit
     * intr-un card care e altfel pasiv: ar baga un control intr-o suprafata fara
     * niciunul, si l-ar pune intr-un `<span>` de o linie.
     *
     * Aceeasi capcana ca avertismentul de la `cardRole`, cu un pas mai departe:
     * acolo problema era ce COLOANA ajunge in card, aici e ce RANDEAZA ea acolo.
     *
     * Fara `cardRender`, cardul foloseste `render`, ca pana acum.
     */
    cardRender?: (value: any, row: T) => React.ReactNode;
}

/**
 * Mesajul implicit de gol, comun cu `ReportMobileSummary` — o singura sursa,
 * ca sa nu mai existe doua texte diferite pentru aceeasi stare (desktop
 * spunea "Nu există date", mobil "Nu există date.").
 */
export const DEFAULT_EMPTY_MESSAGE = 'Nu există date.';

interface DataTableProps<T> {
    columns: Column<T>[];
    data: T[];
    onSort?: (field: string, direction: 'asc' | 'desc') => void;
    currentSort?: string | null; // "field,direction" format
    onFilter?: (field: string, value: string) => void;
    filterValues?: Record<string, string>;
    /** Optional per-row className (e.g. to highlight ranked rows). Default: unchanged behavior. */
    rowClassName?: (row: T, index: number) => string | undefined;
    /** Textul starii de gol. Implicit `DEFAULT_EMPTY_MESSAGE`. */
    emptyMessage?: string;
}

export function DataTable<T extends Record<string, any>>({
    columns,
    data,
    onSort,
    currentSort,
    onFilter,
    filterValues,
    rowClassName,
    emptyMessage
}: DataTableProps<T>) {
    // Vezi `Column.sticky`: o singura coloana poate fi inghetata, fiindca toate
    // ar fi ancorate la aceeasi margine.
    const stickyKey = React.useMemo(
        () => columns.find((column) => column.sticky)?.key,
        [columns]
    );

    const handleSort = (column: Column<T>) => {
        if (!column.sortable || !onSort) return;

        // Check if this column is currently sorted
        const isCurrentSort = currentSort?.includes(column.key);
        const currentDirection = currentSort?.split(',')[1];

        // Toggle: none -> asc -> desc -> asc
        const direction =
            isCurrentSort && currentDirection === 'asc'
                ? 'desc'
                : 'asc';

        onSort(column.key, direction);
    };

    const getSortState = (columnKey: string): { active: boolean; direction?: 'asc' | 'desc' } => {
        if (!currentSort || !currentSort.includes(columnKey)) {
            return { active: false };
        }
        const direction = currentSort.split(',')[1] as 'asc' | 'desc';
        return { active: true, direction };
    };

    return (
        <Table className="min-w-full">
                <TableHeader className="bg-gray-50 sticky top-0 z-20 shadow-sm border-b border-gray-200">
                    <TableRow>
                        {columns.map((column) => {
                            const sortState = getSortState(column.key);
                            return (
                                <TableHead
                                    key={column.key}
                                    className={cn(
                                        "ecotic-table-header-cell",
                                        column.align === 'right' && "text-right",
                                        column.key === stickyKey && "ecotic-table-sticky-cell"
                                    )}
                                >
                                    {/* Row 1: Sort button + Label */}
                                    <div className={cn(
                                        "ecotic-table-sort-row",
                                        column.align === 'right' && "justify-end"
                                    )}>
                                        {column.sortable && (
                                            <TableSortButton
                                                active={sortState.active}
                                                direction={sortState.direction}
                                                onClick={() => handleSort(column)}
                                            />
                                        )}
                                        <span className="text-nowrap font-bold">{column.label}</span>
                                    </div>

                                    {/* Row 2: Filter input (if filterable) */}
                                    {column.filterable && onFilter && (
                                        column.filterType === 'boolean' ? (
                                            <TableBooleanFilter
                                                value={filterValues?.[column.key]}
                                                onChange={(value) => onFilter(column.key, value)}
                                            />
                                        ) : column.filterType === 'dateRange' && column.dateRangeFromName && column.dateRangeToName ? (
                                            <TableDateRangeFilter
                                                fromName={column.dateRangeFromName}
                                                toName={column.dateRangeToName}
                                                from={filterValues?.[column.dateRangeFromName] || ''}
                                                to={filterValues?.[column.dateRangeToName] || ''}
                                                handleChange={(e) => onFilter(e.target.name, e.target.value)}
                                            />
                                        ) : (
                                            <TableSearchInput
                                                placeholder={column.filterPlaceholder || `${column.label}...`}
                                                value={filterValues?.[column.key] || ''}
                                                onChange={(value) => onFilter(column.key, value)}
                                                type={column.filterType as 'text' | 'number' | undefined}
                                            />
                                        )
                                    )}
                                </TableHead>
                            );
                        })}
                    </TableRow>
                </TableHeader>
                <TableBody className="bg-white">
                    {data.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                className="ecotic-table-body-cell text-gray-500"
                            >
                                {/* `sticky left-0`, nu `text-center` pe toata celula: la un
                                    tabel lat (colSpan = suma coloanelor), un mesaj centrat
                                    se aseaza in afara ferestrei vizibile de scroll orizontal
                                    daca userul a derulat spre dreapta, sau chiar la scroll 0
                                    daca tabelul e mai lat decat viewportul. Ancorat la marginea
                                    din stanga a containerului cu scroll (`Table` din
                                    components/ui/table.tsx), la fel ca `ecotic-table-sticky-cell`,
                                    dar pe un <div> copil, nu pe celula intreaga. */}
                                <div className="sticky left-0 w-fit">
                                    {emptyMessage ?? DEFAULT_EMPTY_MESSAGE}
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : (
                        data.map((row, rowIndex) => (
                            <TableRow key={rowIndex} className={cn("ecotic-table-row-hover", rowClassName?.(row, rowIndex))}>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.key}
                                        className={cn(
                                            "ecotic-table-body-cell",
                                            column.align === 'right' && "ecotic-table-cell-numeric",
                                            column.key === stickyKey && "ecotic-table-sticky-cell"
                                        )}
                                    >
                                        {column.render
                                            ? column.render(row[column.key], row)
                                            : row[column.key] || '-'
                                        }
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
    );
}